import { zodResolver } from '@hookform/resolvers/zod'
import { EditableRefKind, refFields, type RefItem } from '@numistic/contracts'
import { useMeta, useRefList, useRefNames, useSaveRef } from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Field,
  Input,
  Select,
  Table,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '@numistic/ui'
import { createRoute, Link, useNavigate } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import { useErrorText } from '../../lib/errors.js'
import { adminRoute } from './layout.js'

export const adminReferencesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/references',
  validateSearch: z.object({ kind: EditableRefKind.catch('issuers') }),
  component: ReferencesPage,
})

type Column = {
  key: string
  kind: 'text' | 'year' | 'int' | 'issuer' | 'url' | 'types'
  table?: boolean
}

/** Описание UI справочников: какие поля показывать и как их редактировать. */
const REF_UI: Record<EditableRefKind, Column[]> = {
  issuers: [
    { key: 'code', kind: 'text', table: true },
    { key: 'yearFrom', kind: 'year', table: true },
    { key: 'yearTo', kind: 'year', table: true },
    { key: 'numistaId', kind: 'text' },
  ],
  periods: [
    { key: 'issuerId', kind: 'issuer', table: true },
    { key: 'yearFrom', kind: 'year', table: true },
    { key: 'yearTo', kind: 'year', table: true },
  ],
  currency_units: [{ key: 'issuerId', kind: 'issuer', table: true }],
  mints: [
    { key: 'issuerId', kind: 'issuer', table: true },
    { key: 'mark', kind: 'text', table: true },
  ],
  printers: [{ key: 'issuerId', kind: 'issuer', table: true }],
  grading_services: [
    { key: 'code', kind: 'text', table: true },
    { key: 'url', kind: 'url' },
  ],
  numbering_systems: [
    { key: 'code', kind: 'text', table: true },
    { key: 'appliesTo', kind: 'types', table: true },
    { key: 'urlTemplate', kind: 'text' },
  ],
}

const NUMERIC_KINDS = new Set(['year', 'int'])

/** Форма хранит строки; перед валидацией контрактом приводим '' → null и строки-числа → числа. */
type FormValues = Record<string, unknown>

function formSchema(kind: EditableRefKind): z.ZodType<FormValues, FormValues> {
  const columns = REF_UI[kind]
  return z.preprocess((raw: FormValues) => {
    const v = { ...raw }
    const name = v.name as { ru?: string; en?: string } | undefined
    v.name = { ru: name?.ru ?? '', ...(name?.en ? { en: name.en } : {}) }
    for (const c of columns) {
      const value = v[c.key]
      if (c.kind === 'types') continue
      if (value === '' || value === undefined) v[c.key] = null
      else if (NUMERIC_KINDS.has(c.kind)) v[c.key] = Number(value)
    }
    return v
  }, refFields[kind]) as z.ZodType<FormValues, FormValues>
}

function toFormValues(
  kind: EditableRefKind,
  item?: RefItem<EditableRefKind>,
): Record<string, unknown> {
  const values: Record<string, unknown> = {
    name: { ru: item?.name.ru ?? '', en: item?.name.en ?? '' },
  }
  for (const c of REF_UI[kind]) {
    const v = (item as Record<string, unknown> | undefined)?.[c.key]
    values[c.key] = c.kind === 'types' ? (v ?? []) : v === null || v === undefined ? '' : String(v)
  }
  return values
}

function ReferencesPage() {
  const { t } = useTranslation()
  const { kind } = adminReferencesRoute.useSearch()
  const [editing, setEditing] = useState<RefItem<EditableRefKind> | 'new' | null>(null)

  return (
    <div className="grid gap-6 lg:grid-cols-[12rem_1fr]">
      <nav className="flex flex-wrap gap-1 lg:flex-col" aria-label={t('admin.referencesTitle')}>
        {EditableRefKind.options.map((k) => (
          <Link
            key={k}
            to="/admin/references"
            search={{ kind: k }}
            onClick={() => setEditing(null)}
            className="rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:bg-accent hover:text-foreground aria-[current=page]:bg-accent aria-[current=page]:text-foreground"
            aria-current={k === kind ? 'page' : undefined}
          >
            {t(`admin.refKinds.${k}`)}
          </Link>
        ))}
      </nav>
      <div className="grid gap-6">
        {editing ? (
          <RefForm
            key={editing === 'new' ? 'new' : editing.id}
            kind={kind}
            item={editing === 'new' ? undefined : editing}
            onDone={() => setEditing(null)}
          />
        ) : null}
        <RefTable kind={kind} onEdit={setEditing} />
      </div>
    </div>
  )
}

function RefTable({
  kind,
  onEdit,
}: {
  kind: EditableRefKind
  onEdit: (item: RefItem<EditableRefKind> | 'new') => void
}) {
  const { t } = useTranslation()
  const errorText = useErrorText()
  const [showInactive, setShowInactive] = useState(false)
  const [query, setQuery] = useState('')
  const list = useRefList(kind, { includeInactive: showInactive })
  const save = useSaveRef(kind)
  const issuerNames = useRefNames(
    'issuers',
    REF_UI[kind].some((c) => c.kind === 'issuer'),
  )
  const meta = useMeta().data
  const columns = REF_UI[kind].filter((c) => c.table)

  const rows = useMemo(() => {
    const q = query.trim().toLocaleLowerCase('ru')
    const items = (list.data ?? []) as Array<RefItem<EditableRefKind> & Record<string, unknown>>
    return q
      ? items.filter((i) =>
          `${i.name.ru} ${i.name.en ?? ''} ${String(i.code ?? '')}`
            .toLocaleLowerCase('ru')
            .includes(q),
        )
      : items
  }, [list.data, query])

  const cell = (item: Record<string, unknown>, c: Column) => {
    const v = item[c.key]
    if (c.kind === 'issuer') return v ? (issuerNames.get(String(v)) ?? '—') : '—'
    if (c.kind === 'types') {
      const codes = v as string[]
      return codes.length
        ? codes
            .map((code) => meta?.itemTypes.find((x) => x.code === code)?.name.ru ?? code)
            .join(', ')
        : '—'
    }
    return v === null || v === undefined || v === '' ? '—' : String(v)
  }

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center gap-3">
        <CardTitle className="mr-auto">{t(`admin.refKinds.${kind}`)}</CardTitle>
        <Input
          className="w-56"
          placeholder={t('common.search')}
          aria-label={t('common.search')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <Checkbox checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          {t('admin.refShowInactive')}
        </label>
        <Button size="sm" onClick={() => onEdit('new')}>
          {t('admin.refNew')}
        </Button>
      </CardHeader>
      <CardContent>
        {(list.error ?? save.error) ? (
          <Alert variant="destructive">{errorText.request(list.error ?? save.error)}</Alert>
        ) : null}
        <Table>
          <THead>
            <Tr>
              <Th>{t('admin.refFields.name')}</Th>
              {columns.map((c) => (
                <Th key={c.key}>{t(`admin.refFields.${c.key}` as 'admin.refFields.code')}</Th>
              ))}
              <Th />
            </Tr>
          </THead>
          <TBody>
            {rows.map((item) => (
              <Tr key={item.id} className={item.isActive ? undefined : 'opacity-60'}>
                <Td className="font-medium">
                  {item.name.ru}
                  {item.isActive ? null : (
                    <Badge variant="muted" className="ml-2">
                      {t('admin.refInactive')}
                    </Badge>
                  )}
                </Td>
                {columns.map((c) => (
                  <Td key={c.key} className="text-muted-foreground">
                    {cell(item, c)}
                  </Td>
                ))}
                <Td className="whitespace-nowrap text-right">
                  <Button variant="ghost" size="sm" onClick={() => onEdit(item)}>
                    {t('common.edit')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={save.isPending}
                    onClick={() =>
                      save.mutate({ id: item.id, input: { isActive: !item.isActive } })
                    }
                  >
                    {item.isActive ? t('admin.refDeactivate') : t('admin.refActivate')}
                  </Button>
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      </CardContent>
    </Card>
  )
}

function RefForm({
  kind,
  item,
  onDone,
}: {
  kind: EditableRefKind
  item?: RefItem<EditableRefKind>
  onDone: () => void
}) {
  const { t } = useTranslation()
  const errorText = useErrorText()
  const save = useSaveRef(kind)
  const issuers = useRefList('issuers', { enabled: REF_UI[kind].some((c) => c.kind === 'issuer') })
  const meta = useMeta().data
  const form = useForm({
    resolver: zodResolver(formSchema(kind)),
    defaultValues: toFormValues(kind, item),
  })
  const errors = form.formState.errors as Record<string, unknown>
  const navigate = useNavigate()

  const onSubmit = form.handleSubmit((input) =>
    save.mutate(
      { id: item?.id, input: input as never },
      {
        onSuccess: () => {
          onDone()
          void navigate({ to: '/admin/references', search: { kind } })
        },
      },
    ),
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle>{item ? t('admin.refEdit') : t('admin.refNew')}</CardTitle>
      </CardHeader>
      <CardContent>
        <form className="grid gap-4 md:grid-cols-2" onSubmit={onSubmit} noValidate>
          {save.error ? (
            <Alert variant="destructive" className="md:col-span-2">
              {errorText.request(save.error)}
            </Alert>
          ) : null}
          <Field
            label={t('admin.refFields.name')}
            error={errorText.field((errors.name as { ru?: never } | undefined)?.ru)}
          >
            <Input autoFocus {...form.register('name.ru')} />
          </Field>
          <Field label={t('admin.refFields.nameEn')}>
            <Input {...form.register('name.en')} />
          </Field>
          {REF_UI[kind].map((c) => {
            const label = t(`admin.refFields.${c.key}` as 'admin.refFields.code')
            const error = errorText.field(errors[c.key] as never)
            if (c.kind === 'issuer')
              return (
                <Field key={c.key} label={label} error={error}>
                  <Select {...form.register(c.key)}>
                    <option value="">{t('common.notSet')}</option>
                    {issuers.data?.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.name.ru}
                      </option>
                    ))}
                  </Select>
                </Field>
              )
            if (c.kind === 'types')
              return (
                <Controller
                  key={c.key}
                  name={c.key}
                  control={form.control}
                  render={({ field }) => (
                    <fieldset className="grid gap-1.5 md:col-span-2">
                      <legend className="text-sm font-medium">{label}</legend>
                      <p className="text-xs text-muted-foreground">{t('admin.refAppliesToHint')}</p>
                      <div className="flex flex-wrap gap-4">
                        {meta?.itemTypes.map((type) => {
                          const selected = (field.value as string[]) ?? []
                          return (
                            <label key={type.code} className="flex items-center gap-2 text-sm">
                              <Checkbox
                                checked={selected.includes(type.code)}
                                onChange={(e) =>
                                  field.onChange(
                                    e.target.checked
                                      ? [...selected, type.code]
                                      : selected.filter((x) => x !== type.code),
                                  )
                                }
                              />
                              {type.name.ru}
                            </label>
                          )
                        })}
                      </div>
                    </fieldset>
                  )}
                />
              )
            return (
              <Field
                key={c.key}
                label={label}
                error={error}
                hint={c.key === 'urlTemplate' ? t('admin.refUrlTemplateHint') : undefined}
              >
                <Input
                  inputMode={NUMERIC_KINDS.has(c.kind) ? 'numeric' : undefined}
                  type={c.kind === 'url' ? 'url' : 'text'}
                  {...form.register(c.key)}
                />
              </Field>
            )
          })}
          <div className="flex gap-2 md:col-span-2">
            <Button type="submit" disabled={save.isPending}>
              {t('common.save')}
            </Button>
            <Button type="button" variant="ghost" onClick={onDone}>
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
